import React from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { Box, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material'
import UserAction from '../user/actions/UserAction'
import CheckIcon from '@mui/icons-material/Check'
import LinkIcon from '@mui/icons-material/Link'
import { CardTitle } from 'components/card/NewCard'
import { Done, HourglassEmptyRounded, QuestionMark } from '@mui/icons-material'
import { favOrange } from 'utils/constants/ColorTheme'
import { OBSERVATORY } from './ObservatoryConstants'
import StarRateRoundedIcon from '@mui/icons-material/StarRateRounded'
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded'
import { getDate } from 'utils/DateUtil'
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined'
import WidgetsOutlinedIcon from '@mui/icons-material/WidgetsOutlined'

const UNKNOWN_STATUS = -1
const MODULE_STATUS = [{
    id: UNKNOWN_STATUS,
    key: 'unknown',
    color: 'unknownStatus',
    icon: <QuestionMark sx={{ fontSize: 12 }} />,
}, {
    id: 1,
    key: 'deployed',
    color: 'validStatus',
    icon: <Done sx={{ fontSize: 12 }} />,
}, {
    id: 2,
    key: 'awaitingDeployment',
    color: 'warningStatus',
    icon: <HourglassEmptyRounded sx={{ fontSize: 12 }} />,
}]

const ModuleCard = ({
    mod = {},
    setModuleToOpen = () => {},
    selected = false,
    deselectable = false,
}) => {
    const {
        contacts,
        accountUser,
        applicativeUsersParameters,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
        accountUser: store.AccountReducer.accountUser,
        applicativeUsersParameters: store.UserReducer.applicativeUsersParameters,
    }), shallowEqual)

    const dispatch = useDispatch()

    const moduleUrl = applicativeUsersParameters.find(s => s.login === mod.login && s.parameter === 'moduleUrl')?.value || ''

    const name = contacts.find(({ code }) => code === mod.contactCode)?.name || mod.login
    const status = MODULE_STATUS.find(({ id }) => id === mod.status) || MODULE_STATUS.find(({ id }) => id === UNKNOWN_STATUS)

    const setBookmark = () => {
        if (mod.isFav) {
            dispatch(UserAction.deleteBookmark({ identifiant: mod.login, login: accountUser.login, stationType: mod.moduleTypeId }))
        } else {
            dispatch(UserAction.createBookmark({ identifiant: mod.login, login: accountUser.login, stationType: mod.moduleTypeId }))
        }
    }

    const starActionProps = {
        sx: {
            color: favOrange,
            fontSize: '2rem',
        },
        onClick: e => {
            e.stopPropagation()
            setBookmark()
        },
    }
    const starAction = mod.isFav ? <StarRateRoundedIcon { ...starActionProps } /> : <StarBorderRoundedIcon { ...starActionProps } />
    const actions = [starAction]

    return (
        <Card
            variant='newCard'
            clickable
            selected={selected}
            deselectable={deselectable}
            onClick={() => setModuleToOpen(mod)}
            component={Stack}
            sx={{ height: '-webkit-fill-available' }}
        >
            <CardTitle
                title={
                    <Typography sx={{ fontSize: '1.25rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: 1 }}>
                        {mod.module === OBSERVATORY.name ? <TravelExploreOutlinedIcon /> : <WidgetsOutlinedIcon />}
                        {name}
                    </Typography>
                }
                actions={actions}
            />
            <CardContent component={Stack} spacing={1.5} sx={{ flex: 1 }}>
                <Grid container justifyContent='space-between' alignItems='center'>
                    {(mod.updateDate && mod.loginMaj) && <Typography color='black60'>{i18n.updatedOn} {getDate(mod.updateDate)} {i18n.by} {mod.loginMaj}.</Typography>}
                    {(!mod.updateDate && mod.loginMaj) && <Typography color='black60'>{i18n.udpdateBy} {mod.loginMaj}.</Typography>}
                    {(mod.updateDate && !mod.loginMaj) && <Typography color='black60'>{i18n.updatedOn} {getDate(mod.updateDate)}.</Typography>}
                    {(!mod.updateDate && !mod.loginMaj) && <Box />}
                    <Chip size='small' label={i18n[status.key]} color={status.color} icon={status.icon}/>
                </Grid>
                <Grid container rowSpacing={0.5} columnSpacing={2}>
                    {mod.activeModules.map(am => (
                        <Grid key={am} container columnSpacing={0.5} alignItems='center'>
                            <CheckIcon />
                            <Typography color='black80'>{am}</Typography>
                        </Grid>
                    ))}
                </Grid>
                <Grid container columnSpacing={0.5} alignItems='center'>
                    <LinkIcon />
                    <Typography color='black80'>https://{moduleUrl}</Typography>
                </Grid>
            </CardContent>
        </Card>
    )
}

ModuleCard.propTypes = {
    mod: PropTypes.shape({
        login: PropTypes.string,
        moduleTypeId: PropTypes.number,
    }),
    setModuleToOpen: PropTypes.func,
    selected: PropTypes.bool,
    deselectable: PropTypes.bool,
}

export default ModuleCard